import type { HttpErrorResponse } from '@angular/common/http';

import { createSelector } from '@ngrx/store';

import type { OfferTag } from '@core/bulk-actions-types';
import type { MetaScopeOptions } from '@core/ngrx-legacy';
import {
  createEntityCollectionStore,
  memoizeSelector,
  ngrxEvent,
  pipeState,
} from '@core/ngrx-legacy';

import { EntityStoreKey } from '../../types';

const { actions, selectors, reducer, namespace } = createEntityCollectionStore<OfferTag>()(
  EntityStoreKey.OfferTags,
  {
    customActions: adapter => ({
      createOfferTag: (state, payload: string | string[]) => adapter.markAsPending(state),
      createOfferTagSuccess: (state, payload: OfferTag[]) =>
        pipeState(adapter.addMany(state, payload), next => adapter.markAsLoaded(next)),
      createOfferTagFail: state => adapter.markAsLoaded(state),
      deleteOfferTags: (state, payload: Array<OfferTag['id']>) => adapter.markAsPending(state),
      deleteOfferTagsSuccess: (state, payload: number[]) =>
        pipeState(adapter.removeMany(state, payload), next => adapter.markAsLoaded(next)),
      updateOfferTag: (
        state,
        payload: {
          tagId: number;
          tagName: string;
        },
      ) => adapter.markAsPending(state),
      updateOfferTagSuccess: (state, payload: OfferTag) =>
        pipeState(adapter.updateOne(state, payload), next => adapter.markAsLoaded(next)),
      updateOfferTagFailed: ngrxEvent<HttpErrorResponse>(),
      mergeOfferTags: (
        state,
        payload: {
          tagIds: Array<number | string>;
          tagName: string;
        },
      ) => adapter.markAsPending(state),
      mergeOfferTagsSuccess: (
        state,
        payload: { offerTag: OfferTag; tagIds: Array<number | string> },
      ) =>
        pipeState(
          adapter.removeMany(state, payload.tagIds),
          next => adapter.addOne(next, payload.offerTag),
          next => adapter.markAsLoaded(next),
        ),
    }),
  },
);

const paginatedAttributeSelector = memoizeSelector((props?: MetaScopeOptions) =>
  createSelector(
    selectors.pagination(props),
    selectors.entities(props),
    (pagination, entities) => ({
      meta: pagination,
      rows: entities.map(
        ({
          id,
          name,
          taggingsCount,
          offerTemplateTaggingsCount,
          offerTaggingsCount,
          status,
          searchableName,
        }) => ({
          id,
          count: offerTaggingsCount ?? taggingsCount,
          jobTemplatesCount: offerTemplateTaggingsCount || 0,
          value: name,
          status,
          searchableName,
        }),
      ),
    }),
  ),
);

const offerTagsSelectors = { ...selectors, paginatedAttributeSelector };

export {
  actions as OfferTagsActions,
  reducer as OfferTagsReducer,
  offerTagsSelectors,
  namespace as OfferTagsNamespace,
};
